/* ==========================================================================
   Okiru Theme - Main JavaScript
   Aligned with Thrive reference theme behavior
   Vanilla JS, no dependencies
   ========================================================================== */

(function () {
  'use strict';

  /* ========================================================================
     Utility Functions
     ======================================================================== */

  function debounce(fn, delay = 250) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  /**
   * Format money - supports Indian numbering format
   * @param {number} cents - Amount in cents (paise)
   * @param {string} format - Money format string from Shopify
   * @returns {string}
   */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');

    const moneyFormat = format || window.okiru?.moneyFormat || '&#8377;{{amount}}';
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function addCommas(n) {
      return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    /* Indian numbering: last 3 digits, then groups of 2 */
    function addIndianCommas(n) {
      const str = n.toString();
      if (str.length <= 3) return str;
      const last3 = str.slice(-3);
      const rest = str.slice(0, -3);
      return rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + last3;
    }

    switch (moneyFormat.match(placeholderRegex)?.[1]) {
      case 'amount':
        value = addCommas((cents / 100).toFixed(2));
        break;
      case 'amount_no_decimals':
        value = addCommas(Math.round(cents / 100));
        break;
      case 'amount_with_comma_separator':
        value = (cents / 100).toFixed(2).replace(/\./, ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = addCommas(Math.round(cents / 100)).replace(/\./, ',');
        break;
      case 'amount_indian':
        value = addIndianCommas((cents / 100).toFixed(2).split('.')[0]) + '.' + (cents / 100).toFixed(2).split('.')[1];
        break;
      case 'amount_indian_no_decimals':
        value = addIndianCommas(Math.round(cents / 100));
        break;
      default:
        value = addIndianCommas((cents / 100).toFixed(2).split('.')[0]) + '.' + (cents / 100).toFixed(2).split('.')[1];
    }

    return moneyFormat.replace(placeholderRegex, value);
  }

  /**
   * Return a fetch config object
   * @param {string} type - 'json' or 'default'
   * @returns {Object}
   */
  function fetchConfig(type = 'json') {
    if (type === 'json') {
      return {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      };
    }
    return {
      method: 'GET',
      headers: { Accept: 'application/json' },
    };
  }

  /* ========================================================================
     Expose globals
     ======================================================================== */

  window.okiru = window.okiru || {};
  window.okiru.utils = { debounce, formatMoney, fetchConfig };

  /* ========================================================================
     Header (Sticky with fix/ready classes, logo shrink)
     ======================================================================== */

  const Header = {
    init() {
      this.header = document.querySelector('.header');
      if (!this.header) return;

      this.scrollThreshold = 80;
      this.lastScrollY = 0;
      this.isDesktop = window.matchMedia('(min-width: 990px)');

      this.applyTransitions();
      this.isDesktop.addEventListener('change', () => this.applyTransitions());

      this.bindStickyHeader();
      this.bindMobileMenu();
      this.bindSearchToggle();
      this.bindMegaMenu();
    },

    /**
     * Set CSS transitions matching Thrive reference:
     * - transform 0.1s linear for the slide-in
     * - Desktop: left 0.5s ease-in-out
     * - Mobile: left 0.3s ease-in-out
     */
    applyTransitions() {
      if (this.isDesktop.matches) {
        this.header.style.transition = 'transform 0.1s linear, left 0.5s ease-in-out';
      } else {
        this.header.style.transition = 'transform 0.1s linear, left 0.3s ease-in-out';
      }
    },

    /**
     * Sticky header behavior:
     * - Adds "fix" when past threshold
     * - Adds "ready" with translateY(0) to slide in
     * - Logo shrinks to 20px max-height
     */
    bindStickyHeader() {
      const logo = this.header.querySelector('.header__logo img, .header__logo svg');

      const onScroll = () => {
        const scrollY = window.scrollY;

        if (scrollY > this.scrollThreshold) {
          if (!this.header.classList.contains('fix')) {
            /* First time passing threshold: set translateY(-100%) then add fix */
            this.header.style.transform = 'translateY(-100%)';
            this.header.classList.add('fix');

            /* Force reflow so the transform is applied before transitioning */
            void this.header.offsetHeight;

            /* Now slide in */
            this.header.classList.add('ready');
            this.header.style.transform = 'translateY(0)';
          }

          /* Shrink logo when sticky */
          if (logo) logo.style.maxHeight = '20px';
        } else {
          this.header.classList.remove('fix', 'ready');
          this.header.style.transform = '';
          if (logo) logo.style.maxHeight = '';
        }

        this.lastScrollY = scrollY;
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    },

    /* Mobile menu toggle */
    bindMobileMenu() {
      const toggleBtn = document.querySelector('[data-mobile-menu-toggle]');
      const menu = document.querySelector('[data-mobile-menu]');
      const overlay = document.querySelector('.overlay');

      if (!toggleBtn || !menu) return;

      const open = () => {
        menu.classList.add('is-open');
        toggleBtn.setAttribute('aria-expanded', 'true');
        document.body.classList.add('menu-open');
        if (overlay) overlay.classList.add('is-active');
      };

      const close = () => {
        menu.classList.remove('is-open');
        toggleBtn.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('menu-open');
        if (overlay) overlay.classList.remove('is-active');
      };

      toggleBtn.addEventListener('click', () => {
        menu.classList.contains('is-open') ? close() : open();
      });

      if (overlay) overlay.addEventListener('click', close);

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && menu.classList.contains('is-open')) close();
      });
    },

    /* Search toggle */
    bindSearchToggle() {
      const toggleBtn = document.querySelector('[data-search-toggle]');
      const searchForm = document.querySelector('[data-search-form]');

      if (!toggleBtn || !searchForm) return;

      toggleBtn.addEventListener('click', () => {
        const isVisible = searchForm.classList.contains('is-open');

        if (isVisible) {
          searchForm.classList.remove('is-open');
          toggleBtn.setAttribute('aria-expanded', 'false');
        } else {
          searchForm.classList.add('is-open');
          toggleBtn.setAttribute('aria-expanded', 'true');
          const input = searchForm.querySelector('input[type="search"], input[type="text"]');
          if (input) input.focus();
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && searchForm.classList.contains('is-open')) {
          searchForm.classList.remove('is-open');
          toggleBtn.setAttribute('aria-expanded', 'false');
        }
      });
    },

    /**
     * Mega menu: show on hover with 80ms delay (desktop)
     * Transition: 0.12s linear
     * ARIA: aria-haspopup, aria-expanded
     */
    bindMegaMenu() {
      const megaMenuItems = document.querySelectorAll('[data-mega-menu]');
      if (!megaMenuItems.length) return;

      megaMenuItems.forEach((item) => {
        const trigger = item.querySelector('[data-mega-menu-trigger]');
        const content = item.querySelector('[data-mega-menu-content]');
        if (!trigger || !content) return;

        let showTimeout;
        let hideTimeout;

        /* Ensure ARIA attributes */
        trigger.setAttribute('aria-haspopup', 'true');
        trigger.setAttribute('aria-expanded', 'false');

        /* Set submenu transition */
        content.style.transition = 'opacity 0.12s linear, visibility 0.12s linear';

        const show = () => {
          clearTimeout(hideTimeout);
          showTimeout = setTimeout(() => {
            /* Close other mega menus first */
            megaMenuItems.forEach((other) => {
              if (other !== item) {
                const otherContent = other.querySelector('[data-mega-menu-content]');
                const otherTrigger = other.querySelector('[data-mega-menu-trigger]');
                if (otherContent) otherContent.classList.remove('is-open');
                if (otherTrigger) otherTrigger.setAttribute('aria-expanded', 'false');
              }
            });
            content.classList.add('is-open');
            trigger.setAttribute('aria-expanded', 'true');
          }, 80);
        };

        const hide = () => {
          clearTimeout(showTimeout);
          hideTimeout = setTimeout(() => {
            content.classList.remove('is-open');
            trigger.setAttribute('aria-expanded', 'false');
          }, 80);
        };

        /* Desktop: hover events */
        if (this.isDesktop.matches) {
          item.addEventListener('mouseenter', show);
          item.addEventListener('mouseleave', hide);
        }

        /* Re-evaluate on resize */
        this.isDesktop.addEventListener('change', (e) => {
          if (e.matches) {
            item.addEventListener('mouseenter', show);
            item.addEventListener('mouseleave', hide);
          }
        });

        /* Keyboard: focus within */
        item.addEventListener('focusin', () => {
          clearTimeout(hideTimeout);
          content.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
        });
        item.addEventListener('focusout', (e) => {
          if (!item.contains(e.relatedTarget)) hide();
        });

        /* Keyboard: Enter or Space on trigger */
        trigger.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            const isOpen = content.classList.contains('is-open');
            isOpen ? hide() : show();
          }
          if (e.key === 'Escape') {
            content.classList.remove('is-open');
            trigger.setAttribute('aria-expanded', 'false');
            trigger.focus();
          }
        });

        /* Escape from within the submenu */
        content.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            content.classList.remove('is-open');
            trigger.setAttribute('aria-expanded', 'false');
            trigger.focus();
          }
        });
      });
    },
  };

  /* ========================================================================
     Cart Drawer
     Sidebar slides from right, 0.3s transition
     Events: cart:open, cart:close, cart:updated
     Body scroll lock when open
     ======================================================================== */

  const Cart = {
    init() {
      /* The drawer markup + open/close DOM logic lives in sections/cart-drawer.liquid.
         Cart here only handles cross-page concerns: count badges, refresh, add. */
      this.countBubbles = document.querySelectorAll('[data-cart-count]');
      this.freeShippingBar = document.querySelector('[data-free-shipping-bar]');
      this.freeShippingThreshold = parseInt(
        document.querySelector('[data-free-shipping-threshold]')?.dataset.freeShippingThreshold || '0',
        10
      );

      this.bindEvents();
    },

    bindEvents() {
      /* Open / close triggers — delegate to the drawer IIFE via custom events */
      document.querySelectorAll('[data-cart-open]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      document.querySelectorAll('[data-cart-close]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.close();
        });
      });

      /* Intercept add-to-cart forms when cart_type is drawer */
      this.interceptAddToCartForms();
    },

    isOpen() {
      const sidebar = document.getElementById('site-cart-sidebar');
      return sidebar?.classList.contains('is-open') ?? false;
    },

    open() {
      document.dispatchEvent(new CustomEvent('cart:open'));
    },

    close() {
      document.dispatchEvent(new CustomEvent('cart:close'));
    },

    /**
     * Refresh cart count + drawer markup.
     * Fetches /cart.js for live totals and ?section_id=cart-drawer for fresh
     * server-rendered drawer HTML. Swaps the inner DOM of #site-cart-sidebar so
     * the IIFE inside cart-drawer.liquid keeps its delegated event listeners.
     */
    async refresh() {
      try {
        const [cartRes, sectionRes] = await Promise.all([
          fetch('/cart.js', { headers: { Accept: 'application/json' } }),
          fetch('/?section_id=cart-drawer'),
        ]);
        const cart = await cartRes.json();
        const html = await sectionRes.text();

        this.updateCount(cart.item_count);
        this.updateFreeShippingBar(cart.total_price);

        const sidebar = document.getElementById('site-cart-sidebar');
        if (sidebar) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const fresh = doc.getElementById('site-cart-sidebar');
          if (fresh) sidebar.innerHTML = fresh.innerHTML;
        }

        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      } catch (error) {
        console.error('Okiru: Error refreshing cart', error);
      }
    },

    /**
     * Add item to cart
     * @param {FormData|Object} formData
     */
    async add(formData) {
      const config = fetchConfig();
      let body;

      if (formData instanceof FormData) {
        const obj = {};
        formData.forEach((val, key) => (obj[key] = val));
        body = JSON.stringify(obj);
      } else {
        body = JSON.stringify(formData);
      }

      config.body = body;

      const response = await fetch('/cart/add.js', config);
      const data = await response.json();

      if (!response.ok) {
        const message = data.description || data.message || 'Cart add failed';
        document.dispatchEvent(new CustomEvent('cart:error', { detail: { message } }));
        throw new Error(message);
      }

      await this.refresh();
      this.open();
      return data;
    },

    /**
     * Change line item quantity
     * @param {number} line - 1-indexed line number
     * @param {number} quantity - New quantity (0 to remove)
     */
    async change(line, quantity) {
      try {
        const config = fetchConfig();
        config.body = JSON.stringify({ line, quantity });

        const response = await fetch('/cart/change.js', config);
        const cart = await response.json();

        if (response.ok) {
          await this.refresh();
        } else {
          console.error('Okiru: Cart change error', cart.description || cart.message);
        }
      } catch (error) {
        console.error('Okiru: Error changing cart item', error);
      }
    },

    /**
     * Update cart count bubbles
     */
    updateCount(count) {
      this.countBubbles.forEach((el) => {
        el.textContent = count;
        el.classList.toggle('is-hidden', count === 0);
      });
    },

    /**
     * Free shipping bar calculation
     */
    updateFreeShippingBar(totalPrice) {
      if (!this.freeShippingBar || !this.freeShippingThreshold) return;

      const thresholdCents = this.freeShippingThreshold * 100;
      const remaining = thresholdCents - totalPrice;
      const progress = Math.min((totalPrice / thresholdCents) * 100, 100);

      const progressBar = this.freeShippingBar.querySelector('[data-shipping-progress]');
      const message = this.freeShippingBar.querySelector('[data-shipping-message]');

      if (progressBar) progressBar.style.width = progress + '%';

      if (message) {
        if (remaining <= 0) {
          message.textContent = 'You qualify for free shipping!';
          this.freeShippingBar.classList.add('is-qualified');
        } else {
          message.textContent = 'Add ' + formatMoney(remaining) + ' more for free shipping';
          this.freeShippingBar.classList.remove('is-qualified');
        }
      }
    },

    /**
     * Intercept add-to-cart forms for drawer mode
     */
    interceptAddToCartForms() {
      document.addEventListener('submit', (e) => {
        const form = e.target.closest('form[action="/cart/add"]');
        if (!form) return;
        if (document.body.dataset.cartType !== 'drawer') return;

        e.preventDefault();

        const submitBtn = form.querySelector('[type="submit"]');
        if (submitBtn) submitBtn.classList.add('btn--loading');

        const formData = new FormData(form);
        this.add(formData).finally(() => {
          if (submitBtn) submitBtn.classList.remove('btn--loading');
        });
      });
    },
  };

  /* Expose cart globally */
  window.okiru.cart = Cart;

  /* ========================================================================
     Product Page
     ======================================================================== */

  const ProductPage = {
    init() {
      this.container = document.querySelector('[data-product-page]');
      if (!this.container) return;

      this.bindVariantSelector();
      this.bindImageGallery();
      this.bindStickyAddToCart();
      this.bindPincodeChecker();
      this.bindQuantitySelector();
    },

    /**
     * Variant selector: update price, image, availability, URL (replaceState)
     */
    bindVariantSelector() {
      const variantSelects = this.container.querySelectorAll('[data-variant-option]');
      const variantInput = this.container.querySelector('input[name="id"]');
      const productJson = this.container.querySelector('[data-product-json]');

      if (!variantSelects.length || !productJson) return;

      let product;
      try {
        product = JSON.parse(productJson.textContent);
      } catch (e) {
        console.error('Okiru: Could not parse product JSON', e);
        return;
      }

      const getSelectedVariant = () => {
        const selectedOptions = [];
        variantSelects.forEach((select) => {
          const checked = select.querySelector('input:checked') || select.querySelector('select');
          if (checked) selectedOptions.push(checked.value);
        });

        return product.variants.find((variant) =>
          variant.options.every((opt, i) => opt === selectedOptions[i])
        );
      };

      const updateProduct = (variant) => {
        if (!variant) return;

        /* Update hidden variant ID input */
        if (variantInput) variantInput.value = variant.id;

        /* Update price display */
        const priceEl = this.container.querySelector('[data-product-price]');
        const comparePriceEl = this.container.querySelector('[data-product-compare-price]');
        const saleBadge = this.container.querySelector('[data-product-sale-badge]');

        if (priceEl) priceEl.innerHTML = formatMoney(variant.price);
        if (comparePriceEl) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            comparePriceEl.innerHTML = formatMoney(variant.compare_at_price);
            comparePriceEl.style.display = '';
            if (saleBadge) saleBadge.style.display = '';
          } else {
            comparePriceEl.style.display = 'none';
            if (saleBadge) saleBadge.style.display = 'none';
          }
        }

        /* Update add-to-cart button availability */
        const addToCartBtn = this.container.querySelector('[data-add-to-cart]');
        const addToCartText = this.container.querySelector('[data-add-to-cart-text]');
        if (addToCartBtn && addToCartText) {
          if (variant.available) {
            addToCartBtn.disabled = false;
            addToCartText.textContent = 'Add to Cart';
          } else {
            addToCartBtn.disabled = true;
            addToCartText.textContent = 'Sold Out';
          }
        }

        /* Update URL without triggering navigation */
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());

        /* Update featured image if variant has one */
        if (variant.featured_image) {
          const mainImage = this.container.querySelector('[data-product-main-image] img');
          if (mainImage) {
            mainImage.src = variant.featured_image.src;
            mainImage.srcset = '';
            mainImage.alt = variant.featured_image.alt || product.title;
          }

          /* Also update active thumbnail */
          const thumbnails = this.container.querySelectorAll('[data-product-thumbnail]');
          thumbnails.forEach((t) => {
            t.classList.remove('is-active');
            const thumbSrc = t.dataset.productThumbnail || t.querySelector('img')?.src;
            if (thumbSrc && variant.featured_image.src && thumbSrc.includes(variant.featured_image.src.split('?')[0].split('/').pop())) {
              t.classList.add('is-active');
            }
          });
        }

        document.dispatchEvent(new CustomEvent('variant:changed', { detail: { variant, product } }));
      };

      variantSelects.forEach((select) => {
        select.addEventListener('change', () => {
          const variant = getSelectedVariant();
          updateProduct(variant);
        });
      });
    },

    /**
     * Image gallery: thumbnail click switches main image
     */
    bindImageGallery() {
      const thumbnails = this.container.querySelectorAll('[data-product-thumbnail]');
      const mainImage = this.container.querySelector('[data-product-main-image] img');

      if (!thumbnails.length || !mainImage) return;

      thumbnails.forEach((thumb) => {
        thumb.addEventListener('click', (e) => {
          e.preventDefault();

          const imgSrc = thumb.dataset.productThumbnail || thumb.querySelector('img')?.src;
          const imgAlt = thumb.dataset.alt || thumb.querySelector('img')?.alt || '';

          if (imgSrc) {
            mainImage.src = imgSrc;
            mainImage.srcset = '';
            mainImage.alt = imgAlt;
          }

          thumbnails.forEach((t) => t.classList.remove('is-active'));
          thumb.classList.add('is-active');
        });
      });
    },

    /**
     * Sticky add-to-cart: IntersectionObserver, show when main button out of view
     */
    bindStickyAddToCart() {
      const addToCartBtn = this.container.querySelector('[data-add-to-cart]');
      const stickyBar = document.querySelector('[data-sticky-add-to-cart]');

      if (!addToCartBtn || !stickyBar) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          stickyBar.classList.toggle('is-visible', !entry.isIntersecting);
        },
        { threshold: 0 }
      );

      observer.observe(addToCartBtn);
    },

    /**
     * Pincode checker form handler
     */
    bindPincodeChecker() {
      const form = this.container.querySelector('[data-pincode-form]');
      if (!form) return;

      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const input = form.querySelector('[data-pincode-input]');
        const message = form.querySelector('[data-pincode-message]');

        if (!input || !message) return;

        const pincode = input.value.trim();

        if (!/^\d{6}$/.test(pincode)) {
          message.textContent = 'Please enter a valid 6-digit pincode';
          message.className = 'pincode-message pincode-message--error';
          return;
        }

        message.textContent = 'Checking...';
        message.className = 'pincode-message';

        /* Simulate delivery check (replace with real API call) */
        setTimeout(() => {
          message.textContent = 'Delivery available to ' + pincode + '. Estimated 3-5 business days.';
          message.className = 'pincode-message pincode-message--success';
        }, 500);
      });
    },

    /**
     * Quantity +/- buttons
     */
    bindQuantitySelector() {
      this.container.querySelectorAll('[data-quantity-selector]').forEach((wrapper) => {
        const minusBtn = wrapper.querySelector('[data-quantity-minus]');
        const plusBtn = wrapper.querySelector('[data-quantity-plus]');
        const input = wrapper.querySelector('[data-quantity-input]');

        if (!minusBtn || !plusBtn || !input) return;

        const min = parseInt(input.min, 10) || 1;
        const max = parseInt(input.max, 10) || 9999;

        minusBtn.addEventListener('click', () => {
          const current = parseInt(input.value, 10) || 1;
          input.value = Math.max(min, current - 1);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });

        plusBtn.addEventListener('click', () => {
          const current = parseInt(input.value, 10) || 1;
          input.value = Math.min(max, current + 1);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    },
  };

  /* ========================================================================
     Tabs
     Click tab -> show panel, update aria-selected
     Arrow key navigation between tabs
     Active tab gets filled pill style (CSS handles visuals, JS toggles classes)
     ======================================================================== */

  const Tabs = {
    init() {
      document.querySelectorAll('[data-tabs]').forEach((tabGroup) => {
        const headers = tabGroup.querySelectorAll('[data-tab-header]');
        const panels = tabGroup.querySelectorAll('[data-tab-panel]');

        if (!headers.length || !panels.length) return;

        headers.forEach((header) => {
          header.addEventListener('click', () => {
            const target = header.dataset.tabHeader;

            /* Deactivate all */
            headers.forEach((h) => {
              h.classList.remove('is-active');
              h.setAttribute('aria-selected', 'false');
              h.setAttribute('tabindex', '-1');
            });
            panels.forEach((p) => {
              p.classList.remove('is-active');
              p.setAttribute('aria-hidden', 'true');
            });

            /* Activate selected */
            header.classList.add('is-active');
            header.setAttribute('aria-selected', 'true');
            header.setAttribute('tabindex', '0');

            const targetPanel = tabGroup.querySelector('[data-tab-panel="' + target + '"]');
            if (targetPanel) {
              targetPanel.classList.add('is-active');
              targetPanel.setAttribute('aria-hidden', 'false');
            }
          });

          /* Arrow key navigation between tab headers */
          header.addEventListener('keydown', (e) => {
            const headersArray = Array.from(headers);
            const currentIndex = headersArray.indexOf(header);
            let newIndex;

            if (e.key === 'ArrowRight') {
              newIndex = (currentIndex + 1) % headersArray.length;
            } else if (e.key === 'ArrowLeft') {
              newIndex = (currentIndex - 1 + headersArray.length) % headersArray.length;
            } else if (e.key === 'Home') {
              newIndex = 0;
            } else if (e.key === 'End') {
              newIndex = headersArray.length - 1;
            } else {
              return;
            }

            e.preventDefault();
            headersArray[newIndex].click();
            headersArray[newIndex].focus();
          });
        });
      });
    },
  };

  /* ========================================================================
     Accordion (FAQ)
     One-open-at-a-time by default
     Smooth max-height animation
     Plus/minus icon toggle (rotate 180deg)
     ARIA: aria-expanded, aria-controls
     ======================================================================== */

  const Accordion = {
    init() {
      document.querySelectorAll('[data-accordion]').forEach((accordion) => {
        const items = accordion.querySelectorAll('[data-accordion-item]');

        items.forEach((item) => {
          const trigger = item.querySelector('[data-accordion-trigger]');
          const content = item.querySelector('[data-accordion-content]');

          if (!trigger || !content) return;

          /* Ensure ARIA attributes */
          const contentId = content.id || 'accordion-content-' + Math.random().toString(36).slice(2, 9);
          content.id = contentId;
          trigger.setAttribute('aria-controls', contentId);
          trigger.setAttribute('aria-expanded', item.classList.contains('is-open') ? 'true' : 'false');

          trigger.addEventListener('click', () => {
            const isOpen = item.classList.contains('is-open');

            /* Close siblings if single-open mode (default) */
            if (accordion.dataset.accordion !== 'multi') {
              items.forEach((sibling) => {
                if (sibling !== item && sibling.classList.contains('is-open')) {
                  this.closeItem(sibling);
                }
              });
            }

            if (isOpen) {
              this.closeItem(item);
            } else {
              this.openItem(item);
            }
          });
        });
      });
    },

    openItem(item) {
      const content = item.querySelector('[data-accordion-content]');
      const trigger = item.querySelector('[data-accordion-trigger]');
      const icon = trigger?.querySelector('[data-accordion-icon]');
      if (!content) return;

      item.classList.add('is-open');
      trigger?.setAttribute('aria-expanded', 'true');
      content.setAttribute('aria-hidden', 'false');

      /* Smooth max-height animation */
      content.style.maxHeight = '0px';
      content.style.overflow = 'hidden';
      content.style.display = 'block';

      /* Force reflow */
      void content.offsetHeight;

      const targetHeight = content.scrollHeight;
      content.style.maxHeight = targetHeight + 'px';

      /* Rotate icon 180deg (plus to minus) */
      if (icon) icon.style.transform = 'rotate(180deg)';

      content.addEventListener(
        'transitionend',
        () => {
          content.style.maxHeight = '';
          content.style.overflow = '';
        },
        { once: true }
      );
    },

    closeItem(item) {
      const content = item.querySelector('[data-accordion-content]');
      const trigger = item.querySelector('[data-accordion-trigger]');
      const icon = trigger?.querySelector('[data-accordion-icon]');
      if (!content) return;

      /* Set current height explicitly so transition works */
      content.style.maxHeight = content.scrollHeight + 'px';
      content.style.overflow = 'hidden';

      /* Force reflow then collapse */
      void content.offsetHeight;
      content.style.maxHeight = '0px';

      /* Reset icon rotation */
      if (icon) icon.style.transform = 'rotate(0deg)';

      content.addEventListener(
        'transitionend',
        () => {
          item.classList.remove('is-open');
          trigger?.setAttribute('aria-expanded', 'false');
          content.setAttribute('aria-hidden', 'true');
          content.style.maxHeight = '';
          content.style.overflow = '';
          content.style.display = '';
        },
        { once: true }
      );
    },
  };

  /* ========================================================================
     Touch Swipe Detection Utility
     ======================================================================== */

  function enableSwipe(el, callbacks) {
    let startX = 0;
    let startY = 0;
    const threshold = 50;
    const restraint = 100;

    el.addEventListener('touchstart', (e) => {
      const touch = e.changedTouches[0];
      startX = touch.pageX;
      startY = touch.pageY;
    }, { passive: true });

    el.addEventListener('touchend', (e) => {
      const touch = e.changedTouches[0];
      const distX = touch.pageX - startX;
      const distY = touch.pageY - startY;

      if (Math.abs(distX) >= threshold && Math.abs(distY) <= restraint) {
        if (distX > 0 && callbacks.onSwipeRight) {
          callbacks.onSwipeRight();
        } else if (distX < 0 && callbacks.onSwipeLeft) {
          callbacks.onSwipeLeft();
        }
      }
    }, { passive: true });
  }

  /* ========================================================================
     Hero Slider
     - Fade transitions (opacity), NOT slide/translateX
     - Dot navigation: active dot expands from 9px to 30px width
     - Autoplay with progress animation on active dot (CSS keyframe animate-width)
     - Pause on hover
     - Touch/swipe support
     - Navigation arrows: prev/next buttons
     ======================================================================== */

  const HeroSlider = {
    init() {
      /* Inject the animate-width keyframe once */
      if (!document.getElementById('okiru-slider-keyframes')) {
        const style = document.createElement('style');
        style.id = 'okiru-slider-keyframes';
        style.textContent =
          '@keyframes animate-width{from{width:9px}to{width:30px}}' +
          '.slider-dot{display:inline-block;width:9px;height:9px;border-radius:9px;' +
          'background:rgba(255,255,255,0.5);margin:0 4px;border:none;padding:0;cursor:pointer;' +
          'transition:background 0.1s linear;}' +
          '.slider-dot.is-active{background:#fff;animation:animate-width linear forwards;width:30px;}';
        document.head.appendChild(style);
      }

      document.querySelectorAll('[data-hero-slider]').forEach((slider) => {
        this.setup(slider);
      });
    },

    setup(slider) {
      const slides = slider.querySelectorAll('[data-slide]');
      const dotsContainer = slider.querySelector('[data-slider-dots]');

      if (slides.length <= 1) return;

      let currentIndex = 0;
      let autoplayTimer = null;
      const autoplayDelay = parseInt(slider.dataset.autoplay, 10) || 5000;
      const autoplay = slider.dataset.autoplay !== 'false';

      /* Ensure slides use opacity-based fade: all hidden except first */
      slides.forEach((slide, i) => {
        slide.style.position = i === 0 ? '' : 'absolute';
        slide.style.top = '0';
        slide.style.left = '0';
        slide.style.width = '100%';
        slide.style.opacity = i === 0 ? '1' : '0';
        slide.style.transition = 'opacity 0.1s linear';
        slide.style.pointerEvents = i === 0 ? '' : 'none';
        if (i === 0) slide.classList.add('is-active');
      });

      /* Create dots if container exists */
      if (dotsContainer) {
        dotsContainer.innerHTML = '';
        slides.forEach((_, i) => {
          const dot = document.createElement('button');
          dot.classList.add('slider-dot');
          dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
          if (i === 0) {
            dot.classList.add('is-active');
            dot.style.animationDuration = autoplayDelay + 'ms';
          }
          dot.addEventListener('click', () => {
            stopAutoplay();
            goTo(i);
            startAutoplay();
          });
          dotsContainer.appendChild(dot);
        });
      }

      const dots = dotsContainer ? dotsContainer.querySelectorAll('.slider-dot') : [];

      const goTo = (index) => {
        if (index === currentIndex) return;

        /* Fade out current */
        const currentSlide = slides[currentIndex];
        currentSlide.style.opacity = '0';
        currentSlide.style.pointerEvents = 'none';
        currentSlide.classList.remove('is-active');
        if (dots[currentIndex]) {
          dots[currentIndex].classList.remove('is-active');
          dots[currentIndex].style.animationDuration = '';
        }

        /* Update index (wrapping) */
        currentIndex = ((index % slides.length) + slides.length) % slides.length;

        /* Fade in next */
        const nextSlide = slides[currentIndex];
        nextSlide.style.opacity = '1';
        nextSlide.style.pointerEvents = '';
        nextSlide.classList.add('is-active');
        if (dots[currentIndex]) {
          dots[currentIndex].classList.add('is-active');
          dots[currentIndex].style.animationDuration = autoplayDelay + 'ms';
        }
      };

      const next = () => goTo(currentIndex + 1);
      const prev = () => goTo(currentIndex - 1);

      /* Autoplay */
      const startAutoplay = () => {
        if (!autoplay) return;
        stopAutoplay();
        autoplayTimer = setInterval(next, autoplayDelay);
      };

      const stopAutoplay = () => {
        clearInterval(autoplayTimer);
        autoplayTimer = null;
      };

      startAutoplay();

      /* Pause on hover */
      slider.addEventListener('mouseenter', stopAutoplay);
      slider.addEventListener('mouseleave', startAutoplay);

      /* Pause when tab is hidden */
      document.addEventListener('visibilitychange', () => {
        document.hidden ? stopAutoplay() : startAutoplay();
      });

      /* Arrow buttons */
      const prevBtn = slider.querySelector('[data-slider-prev]');
      const nextBtn = slider.querySelector('[data-slider-next]');
      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          stopAutoplay();
          prev();
          startAutoplay();
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          stopAutoplay();
          next();
          startAutoplay();
        });
      }

      /* Touch swipe */
      enableSwipe(slider, {
        onSwipeLeft: () => { stopAutoplay(); next(); startAutoplay(); },
        onSwipeRight: () => { stopAutoplay(); prev(); startAutoplay(); },
      });
    },
  };

  /* ========================================================================
     Testimonial Carousel (scroll-based)
     ======================================================================== */

  const TestimonialCarousel = {
    init() {
      document.querySelectorAll('[data-testimonial-carousel]').forEach((carousel) => {
        this.setup(carousel);
      });
    },

    setup(carousel) {
      const track = carousel.querySelector('[data-carousel-track]');
      const items = carousel.querySelectorAll('[data-carousel-item]');

      if (!track || items.length <= 1) return;

      let currentIndex = 0;
      let autoplayTimer = null;
      const autoplayDelay = parseInt(carousel.dataset.autoplay, 10) || 4000;

      const scrollToIndex = (index) => {
        currentIndex = ((index % items.length) + items.length) % items.length;
        const targetItem = items[currentIndex];
        track.scrollTo({
          left: targetItem.offsetLeft - track.offsetLeft,
          behavior: 'smooth',
        });
      };

      const next = () => scrollToIndex(currentIndex + 1);
      const prev = () => scrollToIndex(currentIndex - 1);

      const startAutoplay = () => {
        autoplayTimer = setInterval(next, autoplayDelay);
      };
      const stopAutoplay = () => {
        clearInterval(autoplayTimer);
      };

      startAutoplay();

      carousel.addEventListener('mouseenter', stopAutoplay);
      carousel.addEventListener('mouseleave', startAutoplay);

      const prevBtn = carousel.querySelector('[data-carousel-prev]');
      const nextBtn = carousel.querySelector('[data-carousel-next]');
      if (prevBtn) prevBtn.addEventListener('click', () => { stopAutoplay(); prev(); startAutoplay(); });
      if (nextBtn) nextBtn.addEventListener('click', () => { stopAutoplay(); next(); startAutoplay(); });

      enableSwipe(carousel, {
        onSwipeLeft: () => { stopAutoplay(); next(); startAutoplay(); },
        onSwipeRight: () => { stopAutoplay(); prev(); startAutoplay(); },
      });
    },
  };

  /* ========================================================================
     Marquee
     Speed-based animation: calculate duration from content width and speed (170px/s)
     Seamless loop with CSS translateX
     NO pause on hover (reference has pause-on-hover: false)
     Direction: LTR
     ======================================================================== */

  const Marquee = {
    init() {
      document.querySelectorAll('[data-marquee]').forEach((marquee) => {
        this.setup(marquee);
      });
    },

    setup(marquee) {
      const track = marquee.querySelector('[data-marquee-track]');
      if (!track) return;

      const speed = parseInt(marquee.dataset.marqueeSpeed, 10) || 170; /* px per second */

      /* Duplicate content for seamless loop */
      const clone = track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      marquee.appendChild(clone);

      /* Calculate duration based on content width and speed */
      const calculateAndApply = () => {
        const contentWidth = track.scrollWidth;
        const duration = contentWidth / speed;

        /* Inject keyframe for this marquee */
        const keyframeName = 'marquee-' + Math.random().toString(36).slice(2, 9);
        const style = document.createElement('style');
        style.textContent =
          '@keyframes ' + keyframeName + '{from{transform:translateX(0)}to{transform:translateX(-' + contentWidth + 'px)}}';
        document.head.appendChild(style);

        track.style.animation = keyframeName + ' ' + duration + 's linear infinite';
        clone.style.animation = keyframeName + ' ' + duration + 's linear infinite';
      };

      /* Run after layout settles */
      requestAnimationFrame(calculateAndApply);

      /* Recalculate on resize */
      window.addEventListener('resize', debounce(calculateAndApply, 200));
    },
  };

  /* ========================================================================
     Newsletter Popup
     Show after configurable delay (data attribute)
     localStorage with configurable expiry
     Close on: X, overlay, Escape
     Body scroll lock
     ======================================================================== */

  const NewsletterPopup = {
    init() {
      this.popup = document.querySelector('[data-newsletter-popup]');
      if (!this.popup) return;

      const storageKey = 'okiru_newsletter_dismissed';
      const dismissalData = localStorage.getItem(storageKey);

      /* Check if dismissed and not expired */
      if (dismissalData) {
        try {
          const { timestamp, expiryDays } = JSON.parse(dismissalData);
          const expiryMs = (expiryDays || 7) * 24 * 60 * 60 * 1000;
          if (Date.now() - timestamp < expiryMs) return;
        } catch (e) {
          /* Corrupted data, continue to show */
        }
      }

      const delay = parseInt(this.popup.dataset.delay, 10) || 5000;
      const expiryDays = parseInt(this.popup.dataset.expiry, 10) || 7;

      setTimeout(() => this.show(), delay);

      /* Close: X button */
      const closeBtn = this.popup.querySelector('[data-popup-close]');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.dismiss(storageKey, expiryDays));
      }

      /* Close: overlay click */
      this.popup.addEventListener('click', (e) => {
        if (e.target === this.popup) this.dismiss(storageKey, expiryDays);
      });

      /* Close: Escape */
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.popup.classList.contains('is-visible')) {
          this.dismiss(storageKey, expiryDays);
        }
      });
    },

    show() {
      if (!this.popup) return;
      this.popup.classList.add('is-visible');
      this.popup.setAttribute('aria-hidden', 'false');
      /* Body scroll lock */
      document.body.style.overflow = 'hidden';
    },

    dismiss(storageKey, expiryDays) {
      this.popup.classList.remove('is-visible');
      this.popup.setAttribute('aria-hidden', 'true');
      /* Release scroll lock */
      document.body.style.overflow = '';
      localStorage.setItem(
        storageKey,
        JSON.stringify({ timestamp: Date.now(), expiryDays })
      );
    },
  };

  /* ========================================================================
     Announcement Bar
     Dismiss with localStorage (7-day expiry)
     ======================================================================== */

  const AnnouncementBar = {
    init() {
      const bar = document.querySelector('[data-announcement-bar]');
      if (!bar) return;

      const dismissBtn = bar.querySelector('[data-announcement-dismiss]');
      if (!dismissBtn) return;

      const barId = bar.dataset.announcementBar || 'default';
      const storageKey = 'okiru_announcement_' + barId;

      /* Check if dismissed and still within 7-day window */
      const dismissedAt = localStorage.getItem(storageKey);
      if (dismissedAt) {
        const elapsed = Date.now() - parseInt(dismissedAt, 10);
        const sevenDays = 7 * 24 * 60 * 60 * 1000;
        if (elapsed < sevenDays) {
          bar.style.display = 'none';
          return;
        } else {
          /* Expired, remove stale key */
          localStorage.removeItem(storageKey);
        }
      }

      dismissBtn.addEventListener('click', () => {
        bar.style.display = 'none';
        localStorage.setItem(storageKey, Date.now().toString());
      });
    },
  };

  /* ========================================================================
     Scroll Animations
     IntersectionObserver for .animate-on-scroll -> .is-visible
     Respect prefers-reduced-motion
     ======================================================================== */

  const ScrollAnimations = {
    init() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const elements = document.querySelectorAll('.animate-on-scroll');
      if (!elements.length) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              observer.unobserve(entry.target);
            }
          });
        },
        {
          threshold: 0.1,
          rootMargin: '0px 0px -40px 0px',
        }
      );

      elements.forEach((el) => observer.observe(el));
    },
  };

  /* ========================================================================
     Scroll-to-Top Button
     Show after scrolling down
     transform: translateY(200%) -> translateY(0), transition 0.25s linear
     Click: smooth scroll to top
     ======================================================================== */

  const ScrollToTop = {
    init() {
      this.button = document.querySelector('[data-scroll-to-top]');
      if (!this.button) return;

      /* Set initial hidden state and transition */
      this.button.style.transform = 'translateY(200%)';
      this.button.style.transition = 'transform 0.25s linear';

      this.threshold = 300;
      this.visible = false;

      window.addEventListener('scroll', () => {
        const shouldShow = window.scrollY > this.threshold;

        if (shouldShow && !this.visible) {
          this.visible = true;
          this.button.style.transform = 'translateY(0)';
        } else if (!shouldShow && this.visible) {
          this.visible = false;
          this.button.style.transform = 'translateY(200%)';
        }
      }, { passive: true });

      this.button.addEventListener('click', (e) => {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    },
  };

  /* ========================================================================
     Initialize Everything on DOMContentLoaded
     ======================================================================== */

  document.addEventListener('DOMContentLoaded', () => {
    Header.init();
    Cart.init();
    ProductPage.init();
    Tabs.init();
    Accordion.init();
    HeroSlider.init();
    TestimonialCarousel.init();
    Marquee.init();
    NewsletterPopup.init();
    AnnouncementBar.init();
    ScrollAnimations.init();
    ScrollToTop.init();

    /* Mark body as JS-enabled */
    document.documentElement.classList.replace('no-js', 'js');
  });
})();
